/**
 * Professional Service
 * API integration for professional CRUD operations
 */
import apiClient from './apiClient';
import {
  Professional,
  ProfessionalFilters,
  PaginatedProfessionalsResponse,
  CreateProfessionalRequest,
  UpdateProfessionalRequest,
} from '../types/professional';

// Re-export types for convenience
export * from '../types/professional';

export interface ProfessionalListParams {
  page?: number;
  limit?: number;
  search?: string;
  filters?: Partial<ProfessionalFilters>;
}

class ProfessionalService {
  private baseUrl = '/professionals';

  /**
   * Get paginated list of professionals
   */
  async getProfessionals(params: ProfessionalListParams = {}): Promise<PaginatedProfessionalsResponse> {
    const { page = 1, limit = 10, search, filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      queryParams.append('search', search);
    }

    if (filters) {
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.professionalType) queryParams.append('professionalType', filters.professionalType);
      if (filters.isExaminer !== undefined) queryParams.append('isExaminer', String(filters.isExaminer));
      if (filters.isCoordinator !== undefined) queryParams.append('isCoordinator', String(filters.isCoordinator));
    }

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);
    
    // Backend returns: { data: { professionals, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;
    
    return {
      message: 'Profissionais listados com sucesso',
      data: backendData.professionals ?? [],
      pagination: {
        total: backendData.total ?? 0,
        page: backendData.page || page,
        limit: backendData.limit || limit,
        totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
      },
    };
  }

  /**
   * Get professional by ID
   */
  async getProfessionalById(id: string | number): Promise<Professional> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Search professional by CPF
   */
  async getProfessionalByCpf(cpf: string): Promise<Professional> {
    const cleanCpf = cpf.replace(/\D/g, '');
    const response = await apiClient.get(`${this.baseUrl}/search-by-cpf?cpf=${cleanCpf}`);
    return response.data.data || response.data;
  }

  /**
   * Create new professional
   */
  async createProfessional(data: CreateProfessionalRequest): Promise<Professional> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update existing professional
   */
  async updateProfessional(id: string | number, data: UpdateProfessionalRequest): Promise<Professional> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete professional
   */
  async deleteProfessional(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Toggle professional active status
   */
  async toggleProfessionalStatus(id: string | number): Promise<Professional> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);
    return response.data.data || response.data;
  }

  /**
   * Activate professional
   */
  async activateProfessional(id: string | number): Promise<Professional> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/activate`);
    return response.data.data || response.data;
  }

  /**
   * Deactivate professional
   */
  async deactivateProfessional(id: string | number): Promise<Professional> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Get professional statistics
   */
  async getStatistics(): Promise<{
    total: number;
    ativos: number;
    inativos: number;
    porTipo: Record<string, number>;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data.data || response.data;
  }

  /**
   * Get professionals for dropdown (simplified list)
   */
  async getProfessionalsForDropdown(type?: string): Promise<{ id: number; name: string; professionalType?: string }[]> {
    const params = type ? `?professionalType=${type}` : '';
    const response = await apiClient.get(`${this.baseUrl}/dropdown${params}`);
    return response.data.data || response.data;
  }
  /**
   * Get active providers (clinics) for binding selection
   */
  async getProvidersForBinding(): Promise<Array<{ id: number; tradeName: string; legalName: string }>> {
    const response = await apiClient.get('/providers?status=ATIVO', { skipCompanyFilter: true } as any);
    const providers = response.data?.providers ?? response.data ?? [];
    return providers.map((p: Record<string, unknown>) => ({
      id: Number(p['id']),
      tradeName: (p['tradeName'] as string) || (p['legalName'] as string),
      legalName: p['legalName'] as string,
    }));
  }
}

export const professionalService = new ProfessionalService();
